import { useMemo } from 'react';
import { useGetAiWorkerConfigurationsQuery, useTypedSelector } from '@circuitry-ai/doc-data';
import { DEFAULT_SCORE_THRESHOLDS, resolveScoreThresholds } from './claimScoreRendererShared';

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Active `agent_config` for an AI worker. Args match the scoring tab query so RTK Query
 * reuses that cache: the same agent does not refetch, a different agent does.
 * Missing config, or missing `green_min` / `yellow_min`, resolves to 85 / 60.
 */
export const useAgentScoreThresholds = (aiworkerId: string | null | undefined) => {
    const token = useTypedSelector((state) => state.auth.token);
    const queryPayload = useMemo(
        () => ({
            scope: 'tenant' as const,
            pagination: { page: 1, per_page: 10 },
            filters: {
                aiworker_id: aiworkerId ?? '',
                config_name: 'agent_config',
                status: 'active',
                is_active: true,
            },
        }),
        [aiworkerId]
    );

    const skipped = !token || !aiworkerId;
    const { data, isSuccess, isError } = useGetAiWorkerConfigurationsQuery(queryPayload, {
        skip: skipped,
    });

    const thresholds = useMemo(() => {
        const config = data?.configurations?.[0]?.config;
        const workflowData = isRecord(config) && isRecord(config.workflow_data) ? config.workflow_data : null;
        if (!workflowData) return DEFAULT_SCORE_THRESHOLDS;
        // Scoring tab reads this same object: top-level workflow_data, or workflow_data.config when that wrapper is present.
        const nested = isRecord(workflowData.config) ? workflowData.config : null;
        return resolveScoreThresholds(workflowData.score_color_thresholds ?? nested?.score_color_thresholds);
    }, [data]);

    // Wait for the first response so the score is not painted with 85/60 and then recolored.
    return { thresholds, isReady: skipped || isSuccess || isError };
};
